import type { Dispatch, FC, ReactNode } from 'react';
import { createContext, useContext, useMemo, useReducer } from 'react';

import { Action, memberInviteReducer, State } from './reducer';

type Context = State & {
  dispatch: Dispatch<Action>;
};

interface MemberInviteProviderProps {
  children: ReactNode;
}

const MemberInviteContext = createContext<Context | undefined>(undefined);

export const useMemberInviteState = (): Context => {
  const context = useContext(MemberInviteContext);

  if (!context) {
    throw new Error(
      'useMemberInviteState must be used within a <MemberInviteProvider/>',
    );
  }

  return context;
};

const MemberInviteProvider: FC<MemberInviteProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(memberInviteReducer, {
    isFreeRolesOnly: false,
    memberInformation: {
      inviteeEmail: '',
      inviteeFirstName: '',
      inviteeLastName: '',
    },
    memberRole: {
      role: undefined,
    },
    selectedLegalBeneficiary: false,
    shouldShowLegalBeneficiarySelector: true,
  });

  const context = useMemo<Context>(
    () => ({
      ...state,
      dispatch,
    }),
    [state, dispatch],
  );

  return (
    <MemberInviteContext.Provider value={context}>
      {children}
    </MemberInviteContext.Provider>
  );
};

export default MemberInviteProvider;
